import type { CSSProperties } from 'react'
import { useGovernance } from './GovernanceProvider'
import { GOV_ROLES, evaluatePermission, effectiveRole, type GovAction, type GovRole } from './governanceModel'

const MATRIX_ACTIONS: { action: GovAction; label: string }[] = [
  { action: 'view', label: 'View' },
  { action: 'comment', label: 'Comment' },
  { action: 'add_update', label: 'Update' },
  { action: 'assign', label: 'Assign' },
  { action: 'approve', label: 'Approve' },
  { action: 'revoke', label: 'Revoke' },
  { action: 'share', label: 'Share' },
  { action: 'change_policy', label: 'Change policy' },
]

const cellStyle: CSSProperties = { padding: '6px 8px', textAlign: 'center', fontSize: 11, fontFamily: 'var(--sc-font-mono)' }

/**
 * RolePermissionPanel — the effective-permission matrix (7 roles × actions).
 * deny-overrides-allow, visible reasons, and a Test-as-role switch. Honestly
 * labeled: this previews the local governance model; the server enforces the
 * numeric role ranks it maps onto.
 */
export default function RolePermissionPanel({ onClose }: { onClose: () => void }) {
  const gov = useGovernance()
  const current = effectiveRole(gov.state)
  return (
    <div className="olv-gov-modal-backdrop" onClick={onClose} role="presentation">
      <div className="olv-gov-modal olv-gov" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Roles & permissions" style={{ padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: 'var(--govv-identity)' }}>Roles &amp; permissions</h2>
          <button type="button" className="olv-gov-btn" onClick={onClose} aria-label="Close">Close</button>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--govv-ink-3)' }}>
          deny-overrides-allow · scope inheritance · effective permission shown. Previews the local model — the server enforces the numeric role ranks it maps onto. Acting as <strong style={{ color: 'var(--govv-identity)' }}>{current}</strong>.
        </p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 620 }}>
            <thead>
              <tr>
                <th style={{ ...cellStyle, textAlign: 'left' }}>Role</th>
                {MATRIX_ACTIONS.map((a) => <th key={a.action} style={cellStyle}>{a.label}</th>)}
                <th style={cellStyle}>Test</th>
              </tr>
            </thead>
            <tbody>
              {GOV_ROLES.map((role) => (
                <tr key={role} style={{ borderTop: '1px solid var(--govv-line)', background: role === current ? 'var(--sc-surface-2)' : 'transparent' }}>
                  <td style={{ ...cellStyle, textAlign: 'left', fontWeight: role === current ? 700 : 400, color: role === current ? 'var(--govv-identity)' : 'var(--govv-ink)' }}>{role}</td>
                  {MATRIX_ACTIONS.map((a) => {
                    const d = evaluatePermission(role as GovRole, a.action)
                    return (
                      <td key={a.action} style={{ ...cellStyle, color: d.allowed ? 'var(--govv-verified)' : 'var(--govv-denied)' }} title={d.reason}>
                        {d.allowed ? 'allow' : 'deny'}
                      </td>
                    )
                  })}
                  <td style={cellStyle}>
                    <button type="button" className="olv-gov-btn" style={{ padding: '2px 8px', fontSize: 11 }}
                      onClick={() => gov.run({ type: 'set_viewer_role', role: role === current ? null : role })}>
                      {role === current ? 'Acting' : 'Test'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 14, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" className="olv-gov-btn" onClick={() => gov.run({ type: 'set_viewer_role', role: null })}>Reset to actual role</button>
          <span style={{ fontSize: 11, color: 'var(--govv-ink-3)' }}>Exceptions require a reason + expiry (recorded in the local-session ledger).</span>
        </div>
      </div>
    </div>
  )
}
